import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "../component/Header";
import Sidebar from "../component/Sidebar";
import "../css/sidebar.css";
import { NavLink } from "react-router-dom";
import { Tooltip, Whisper } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { Placeholder } from "rsuite";
import TablePageSizeSelect from "../component/TablePageSizeSelect";
import LogoApple from "../images/company-logos/apple.svg";
import LogoIgne from "../images/company-logos/igne.svg";
import LogoTesla from "../images/company-logos/tesla.svg";
import LogoJNJ from "../images/company-logos/jnj.svg";
import SettingIcon from "../images/iconSetting.svg";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { getCurrentLanguage, t, translateColumnLabel } from "../i18n";
import { EllipsisVertical, ArrowUp, ArrowDown } from "lucide-react";
import { createPortal } from "react-dom";

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

function OrderHistory(props) {
  return <OrderHistoryContent {...props} />;
}

const ORDER_HISTORY_PMS_COLUMN_KEY_TO_TABLE_COL = {
  name: "Name",
  script: "ScripCode",
  scrip_code: "ScripCode",
  order_status: "OrderStatus",
  quantity: "Quantity",
  reason: "Reason",
  date: "Date",
  type: "Type",
  price: "Price",
  exch: "Exch",
};

const ORDER_HISTORY_TABLE_COL_LABEL_ALIASES = {
  Script: "ScripCode",
  "Order Status": "OrderStatus",
};

const ORDER_HISTORY_TABLE_COL_TO_PMS_COLUMN_KEY = {
  ...Object.fromEntries(
    Object.entries(ORDER_HISTORY_PMS_COLUMN_KEY_TO_TABLE_COL).map(
      ([apiKey, tableCol]) => [tableCol, apiKey],
    ),
  ),
  ScripCode: "script",
  OrderStatus: "order_status",
};

function mapOrderHistoryPmsColumnToTableCol(columnKey, columnLabel) {
  const key = String(columnKey || "").toLowerCase().trim();
  if (ORDER_HISTORY_PMS_COLUMN_KEY_TO_TABLE_COL[key]) {
    return ORDER_HISTORY_PMS_COLUMN_KEY_TO_TABLE_COL[key];
  }
  const label = String(columnLabel || "").trim();
  if (ORDER_HISTORY_TABLE_COL_LABEL_ALIASES[label]) {
    return ORDER_HISTORY_TABLE_COL_LABEL_ALIASES[label];
  }
  const known = new Set([
    "Name",
    "OrderStatus",
    "Reason",
    "Quantity",
    "ScripCode",
    "Date",
    "Price",
    "Exch",
    "Type",
    "Actions",
  ]);
  if (known.has(label)) return label;
  return null;
}

function buildOrderHistoryTableColumnOrderFromPms(columnsPayload) {
  if (!Array.isArray(columnsPayload) || columnsPayload.length === 0)
    return null;
  const sorted = [...columnsPayload].sort(
    (a, b) => Number(a.sort_order) - Number(b.sort_order),
  );
  const out = [];
  const seen = new Set();
  for (const c of sorted) {
    const selected =
      c.is_selected === "1" || c.is_selected === 1 || c.is_selected === true;
    const active =
      c.is_active === "1" || c.is_active === 1 || c.is_active === true;
    if (!selected || !active) continue;
    const col = mapOrderHistoryPmsColumnToTableCol(c.column_key, c.column_label);
    if (!col || col === "Actions") continue;
    if (seen.has(col)) continue;
    seen.add(col);
    out.push(col);
  }
  if (!out.includes("Actions")) out.push("Actions");
  return out.length ? out : null;
}

function OrderHistoryContent(props) {
  // All initialization and Declaration
  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
  const searchInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [tabName, setTabName] = useState("Order History");
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = (key) => t(key, currentLanguage);
  const [orderBook, setOrderBook] = useState([]);

  const [columns, setColumns] = useState([
    "Name",
    // "Status",
    "OrderStatus",
    "Reason",
    "Quantity",
    "ScripCode",
    "Date",
    "Price",
    "Exch",
    "Type",
    "Actions",
  ]);

  // Drag-N-Drop
  // Add these in your component
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columns); // start with columns prop or state
  const visibleColumnOrder = columnOrder.filter((col) => col !== "Exch");

  // table search
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeHeaderMenu, setActiveHeaderMenu] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });

  class BaseOrderHistoryAdapter {
    stringOrDefault = (value, fallback = "") =>
      value === null || value === undefined || value === "" ? fallback : String(value);

    numberOrDefault = (value, fallback = 0) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : fallback;
    };

    formatDateLabel = (dateValue) => {
      if (!dateValue) return "-";
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return this.stringOrDefault(dateValue, "-");
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    formatDotNetDateLabel = (dotNetDate) => {
      const raw = this.stringOrDefault(dotNetDate, "");
      const match = raw.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//);
      if (!match) return this.formatDateLabel(raw);
      const date = new Date(Number(match[1]));
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    normalize = (history = []) => history;
  }

  // class FivePaisaOrderHistoryAdapter extends BaseOrderHistoryAdapter {
  //   normalize = (history = []) =>
  //     history.map((row = {}) => {
  //       const requestPayload = row?.request_payload || {};
  //       const responsePayload = row?.response_payload || {};
  //       const responseOrder = responsePayload?.order || {};

  //       const specialFailureMessage =
  //         "Please register your static IPs first and if you have done it already then retry after some time.";
  //       const rawOrderMessage = this.stringOrDefault(responseOrder?.Message, "");
  //       const rawStatus = this.stringOrDefault(responsePayload?.status, "unknown");
  //       const derivedStatus =
  //         rawOrderMessage === specialFailureMessage ? "failed" : rawStatus;

  //       const symbol = this.stringOrDefault(
  //         row?.symbol,
  //         this.stringOrDefault(requestPayload?.ScripCode, "-"),
  //       );
  //       const name = this.stringOrDefault(row?.name, symbol);
  //       const priceValue = requestPayload?.Price;

  //       return {
  //         id: this.stringOrDefault(row?.id, ""),
  //         Exch: this.stringOrDefault(requestPayload?.Exchange, "N").toUpperCase(),
  //         ExchType: this.stringOrDefault(requestPayload?.ExchangeType, "C").toUpperCase(),
  //         Name: name,
  //         Symbol: symbol,
  //         FullName: name,
  //         Scripcode: symbol,
  //         watchlist_price: this.numberOrDefault(priceValue, 0),
  //         min_qty: this.numberOrDefault(responsePayload?.Qty, 0),
  //         Qty: this.numberOrDefault(responsePayload?.Qty, 0),
  //         orderDate: this.formatDotNetDateLabel(responseOrder?.Time),
  //         order_type: this.stringOrDefault(requestPayload?.OrderType, "").toUpperCase(),
  //         Type: this.stringOrDefault(requestPayload?.OrderType, "").toUpperCase(),
  //         OrderStatus: derivedStatus.toUpperCase(),
  //         Reason: this.stringOrDefault(rawOrderMessage, "-"),
  //         shariahCompliantStatus: "N/A",
  //         CurrentPrice: this.numberOrDefault(priceValue, 0),
  //       };
  //     });
  // }
  class FivePaisaOrderHistoryAdapter extends BaseOrderHistoryAdapter {
    normalize = (history = []) =>
      history.map((row = {}) => {
        const status = this.stringOrDefault(row?.order_status, "").toUpperCase();
  
        return {
          id: this.stringOrDefault(row?.id, ""),
          Exch: this.stringOrDefault(row?.exch, "N").toUpperCase(),
          ExchType: this.stringOrDefault(row?.exch_type, "C").toUpperCase(),
          Name: this.stringOrDefault(row?.scrip_name, ""),
          Symbol: this.stringOrDefault(row?.scrip_name, ""),
          FullName: this.stringOrDefault(row?.scrip_name, ""),
          Scripcode: this.stringOrDefault(row?.scrip_code, ""),
  
          watchlist_price: this.numberOrDefault(row?.rate, 0),
          min_qty: this.numberOrDefault(row?.qty, 0),
          Qty: this.numberOrDefault(row?.qty, 0),
  
          orderDate: this.formatDotNetDateLabel(row?.broker_order_time),
  
          order_type: this.stringOrDefault(row?.buy_sell, "").toUpperCase(),
          Type: this.stringOrDefault(row?.buy_sell, "").toUpperCase(),
  
          OrderStatus: status,
          Reason: this.stringOrDefault(row?.raw_response?.Reason, "-"),
  
          shariahCompliantStatus: "N/A",
          CurrentPrice: this.numberOrDefault(row?.average_price, 0),
        };
      });
  }

  class AngelOneOrderHistoryAdapter extends BaseOrderHistoryAdapter {
    parseTradingSymbol = (tradingsymbol) => {
      const raw = this.stringOrDefault(tradingsymbol, "");
      const base = raw.includes("-") ? raw.split("-")[0] : raw;
      return { raw, base: this.stringOrDefault(base, raw) };
    };

    normalize = (history = []) =>
      history.map((row = {}) => {
        const tradingSymbol = this.stringOrDefault(
          row?.trading_symbol,
          this.stringOrDefault(row?.symbol, ""),
        );
        const { raw, base } = this.parseTradingSymbol(tradingSymbol);
        const displayName = this.stringOrDefault(row?.name, base || raw || "-");

        const exchangeRaw = this.stringOrDefault(row?.exchange, "NSE").toUpperCase();
        const exch = exchangeRaw.startsWith("B")
          ? "B"
          : exchangeRaw.startsWith("M")
            ? "M"
            : "N";

        const productType = this.stringOrDefault(
          row?.product_type,
          "DELIVERY",
        ).toUpperCase();
        const exchType =
          productType.includes("DELIVERY") || productType === "CNC" ? "C" : "D";

        const transactionType = this.stringOrDefault(
          row?.transaction_type,
          "",
        ).toUpperCase();
        const orderType = this.stringOrDefault(row?.order_type, "").toUpperCase();
        const price = this.numberOrDefault(row?.price, 0);
        const avgPrice = this.numberOrDefault(row?.average_price, 0);

        const orderDateRaw =
          row?.order_date ||
          row?.raw_response?.updatetime ||
          row?.created_at ||
          "";

        return {
          id: this.stringOrDefault(row?.id, ""),
          Exch: exch,
          ExchType: exchType,
          Name: displayName,
          Symbol: base || raw,
          FullName: displayName,
          Scripcode: this.stringOrDefault(row?.symbol_token, raw),

          watchlist_price:
            price === 0 && orderType ? `0 (${orderType})` : price,

          min_qty: this.numberOrDefault(row?.quantity, 0),
          Qty: this.numberOrDefault(row?.quantity, 0),

          orderDate: this.formatDateLabel(orderDateRaw),

          order_type: transactionType,
          Type: transactionType,

          OrderStatus: this.stringOrDefault(
            row?.order_status || row?.status,
            "",
          ).toUpperCase(),
          Reason: this.stringOrDefault(
            row?.reason || row?.raw_response?.text,
            "-",
          ),

          shariahCompliantStatus: "N/A",
          CurrentPrice: avgPrice,
        };
      });
  }

  class GrowOrderHistoryAdapter extends BaseOrderHistoryAdapter {
    normalize = (history = []) =>
      history.map((row = {}) => {
        const exchangeRaw = this.stringOrDefault(row?.exchange, "NSE").toUpperCase();
        const exch = exchangeRaw.startsWith("B")
          ? "B"
          : exchangeRaw.startsWith("M")
          ? "M"
          : "N";
  
        const transactionType = this.stringOrDefault(row?.transaction_type, "").toUpperCase();
        const orderType = this.stringOrDefault(row?.order_type, "").toUpperCase();
  
        const price = this.numberOrDefault(row?.price, 0);
        const avgPrice = this.numberOrDefault(row?.average_fill_price, 0);
  
        return {
          id: this.stringOrDefault(row?.id, ""),
          Exch: exch,
          ExchType: "C",
  
          Name: this.stringOrDefault(row?.trading_symbol, ""),
          Symbol: this.stringOrDefault(row?.trading_symbol, ""),
          FullName: this.stringOrDefault(row?.trading_symbol, ""),
          Scripcode: this.stringOrDefault(row?.trading_symbol, ""),
  
          watchlist_price:
            price === 0 && orderType
              ? `0 (${orderType})`
              : price,
  
          min_qty: this.numberOrDefault(row?.quantity, 0),
          Qty: this.numberOrDefault(row?.quantity, 0),
  
          orderDate: this.formatDateLabel(row?.created_at_broker),
  
          order_type: transactionType,
          Type: transactionType,
  
          OrderStatus: this.stringOrDefault(row?.order_status, "").toUpperCase(),
          Reason: this.stringOrDefault(row?.remark, "-"),
  
          shariahCompliantStatus: "N/A",
          CurrentPrice: avgPrice,
        };
      });
  }

  const getOrderHistoryAdapter = (brokerFlag) => {
    const normalizedFlag = String(brokerFlag || "").toLowerCase();
    if (normalizedFlag === "grow") return new GrowOrderHistoryAdapter();
    if (normalizedFlag === "5paisa") return new FivePaisaOrderHistoryAdapter();
    if (normalizedFlag === "angelone") return new AngelOneOrderHistoryAdapter();
    return new BaseOrderHistoryAdapter();
  };

  //----------------------------// useEffects //----------------------------//

  useEffect(() => {
    document.title = tabName;
  }, [tabName]);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLanguage(event?.detail?.language || getCurrentLanguage());
    };
    window.addEventListener("app-language-change", handleLanguageChange);
    return () =>
      window.removeEventListener("app-language-change", handleLanguageChange);
  }, []);

  useEffect(() => {
    const headerEl = document.getElementById("header");
    const updateStickyTop = () => {
      if (!headerEl) {
        document.documentElement.style.removeProperty(
          "--order-history-thead-top",
        );
        return;
      }
      const offset = headerEl.classList.contains("header_sticky")
        ? `${Math.round(headerEl.getBoundingClientRect().height)}px`
        : "0px";
      document.documentElement.style.setProperty(
        "--order-history-thead-top",
        offset,
      );
    };

    updateStickyTop();
    window.addEventListener("scroll", updateStickyTop, { passive: true });
    window.addEventListener("resize", updateStickyTop);
    const mo = new MutationObserver(updateStickyTop);
    if (headerEl) {
      mo.observe(headerEl, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => {
      window.removeEventListener("scroll", updateStickyTop);
      window.removeEventListener("resize", updateStickyTop);
      mo.disconnect();
      document.documentElement.style.removeProperty(
        "--order-history-thead-top",
      );
    };
  }, []);

  useEffect(() => {
    let columnsToUse = columns || []; // fallback

    // Define async function inside useEffect
    const fetchColumnOrderAndOrders = async () => {
      setProgress(10);
      const brokerFlag = String(
        localStorage.getItem("broker_type") || sharedData?.broker_type || "",
      ).toLowerCase();
      const email = String(
        localStorage.getItem("email") ||
        sharedData?.Email_ID ||
        sharedData?.email ||
        "",
      ).trim();
      const pmsBrokerFlag = toPmsBrokerFlag(brokerFlag);
      try {
        // 1. Fetch column order from PMS
        if (email) {
          const colOrderResponse = await fetch(
            `${pmsApiBaseUrl}/api/v1/columns/list`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                broker_flag: pmsBrokerFlag,
                module_name: "order_history",
                user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
                email,
              }),
            },
          );
          const colOrderPayload = await colOrderResponse.json();
          const colData = colOrderPayload?.data;
          const apiColumns = colData?.columns;
          if (colData?.status === "success" && Array.isArray(apiColumns)) {
            const incomingOrder = buildOrderHistoryTableColumnOrderFromPms(
              apiColumns,
            );
            if (incomingOrder?.length) {
              setColumnOrder(incomingOrder);
              setColumns(incomingOrder);
              columnsToUse = incomingOrder;
            } else {
              console.warn(
                "PMS order history columns empty after mapping, using default columns.",
              );
            }
          } else {
            console.warn("Failed to fetch column order from PMS, using default columns.");
          }
        }
      } catch (error) {
        console.error("Error fetching column order:", error);
      }

      console.log("✅📉Columns is:", columnsToUse);

      setProgress(30);

      // 2. Fetch order book data
      try {
        const adapter = getOrderHistoryAdapter(brokerFlag);

        if (!email || !brokerFlag) {
          setOrderBook([]);
          setIsLoading(false);
          setProgress(100);
          return;
        }

        const dataResponse = await fetch(
          `${pmsApiBaseUrl}/api/v1/order_history`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email,
              broker_flag: brokerFlag,
              page: 1,
              limit: 50,
              order: "desc",
            }),
          },
        );
        if (!dataResponse.ok) throw new Error("Network response was not ok");
        const data = await dataResponse.json();
        const payload = data?.data || {};
        const history = Array.isArray(payload?.history) ? payload.history : [];
        const status = String(payload?.status || "").toLowerCase();

        if (status !== "success" || history.length === 0) {
          console.warn("No orders found");
          setOrderBook([]); // clear existing orders
        } else {
          setOrderBook(adapter.normalize(history));
        }
        setIsLoading(false);
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
        setOrderBook([]);
        setIsLoading(false);
      }
      setProgress(100);
    };

    fetchColumnOrderAndOrders();
    // No dependency means only on mount
  }, []);

  useEffect(() => {
    console.log("💗💗💗setSearchQuery: ", searchQuery);
    console.log("💗💗💗filteredOrderBook: ", filteredOrderBook);
    console.log("💗💗💗currentItems: ", currentItems);
  }, [searchQuery]);

  //----------------------------// Functions //----------------------------//

  //----------------------------// table Search Functions //----------------------------//

  //  const filteredOrderBook = orderBook.filter((order) => {
  //   if (!searchQuery) return true;

  //   const searchLower = searchQuery.toLowerCase();
  //   const name = (order.Name || "").toLowerCase();
  //   const scripName = (order.FullName || "").toLowerCase();

  //   return name.includes(searchLower) || scripName.includes(searchLower);
  // });

  // const filteredOrderBook = orderBook.filter((order) => {
  //   if (!searchQuery) return true;

  //   const searchLower = searchQuery.toLowerCase();

  //   // Check multiple possible property names to match your data structure
  //   const name = ( order.Symbol || "").toLowerCase();
  //   const fullName = (order.FullName ||  "").toLowerCase();

  //   return fullName.includes(searchLower) || name.includes(searchLower);
  // });

  const filteredOrderBook = orderBook.filter((order) => {
    if (!searchQuery.trim()) return true;

    const searchLower = searchQuery.toLowerCase();
    const name = (order.Symbol || order.Name || "").toLowerCase();
    const fullName = (order.FullName || "").toLowerCase();

    return fullName.includes(searchLower) || name.includes(searchLower);
  });

  const getOrderHistorySortableValue = useCallback((order, columnKey) => {
    if (!order) return "";

    // Map column names to actual data property names used in table rendering
    const propertyMap = {
      "Date": ["orderDate"],
      "Price": ["watchlist_price", "CurrentPrice"],
      "Quantity": ["Qty", "min_qty"],
      "ScripCode": ["Scripcode", "ScripCode"],
      "Name": ["FullName", "Name", "Symbol"],
      "OrderStatus": ["OrderStatus"],
      "Reason": ["Reason"],
      "Exch": ["Exch"],
      "Type": ["Type", "order_type"],
    };

    const props = propertyMap[columnKey] || [columnKey];
    let raw = "";

    for (const prop of props) {
      if (order?.[prop] !== undefined && order?.[prop] !== null && order?.[prop] !== "") {
        raw = order[prop];
        break;
      }
    }

    if (!raw) {
      raw = order?.[columnKey] ?? "";
    }

    const num = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(num) && raw !== "" ? num : String(raw).toLowerCase();
  }, []);

  const sortedOrderHistory = useMemo(() => {
    if (!sortConfig || !sortConfig.column || !sortConfig.direction) {
      return filteredOrderBook;
    }
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredOrderBook].sort((a, b) => {
      const aVal = getOrderHistorySortableValue(a, sortConfig.column);
      const bVal = getOrderHistorySortableValue(b, sortConfig.column);
      if (typeof aVal === "number" && typeof bVal === "number") return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [filteredOrderBook, getOrderHistorySortableValue, sortConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrderBook.length]);

  //----------------------------// End table Search Functions //----------------------------//

  //----------------------------// Pagination Functions //----------------------------//

  // Pagination calculations
  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedOrderHistory.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(sortedOrderHistory.length / pageSize);

  useEffect(() => {
    if (!activeHeaderMenu) return undefined;
    const handleClose = () => setActiveHeaderMenu(null);
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [activeHeaderMenu]);

  const renderHeaderMenu = useCallback((col) => {
    const isOpen = activeHeaderMenu?.column === col;
    const selectedDirection = sortConfig?.column === col ? sortConfig?.direction : null;
    const menu = isOpen ? (
      <div onMouseDown={(e) => e.stopPropagation()} style={{ position: "fixed", top: activeHeaderMenu?.top ?? 0, left: activeHeaderMenu?.left ?? 0, width: 184, background: "#ffffff", borderRadius: 12, border: "1px solid #dde3ea", boxShadow: "0 10px 24px rgba(18, 34, 51, 0.18)", padding: "7px", zIndex: 1200 }}>
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "asc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "asc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowUp size={14} /><span>Sort Ascending</span></span></button>
        <button type="button" onClick={() => { setSortConfig({ column: col, direction: "desc" }); setCurrentPage(1); setActiveHeaderMenu(null); }} style={{ width: "100%", border: "none", background: selectedDirection === "desc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowDown size={14} /><span>Sort Descending</span></span></button>
      </div>
    ) : null;
    return (
      <div style={{ position: "absolute", right: col === "Name" ? 4 : 6, top: "50%", transform: "translateY(-50%)" }}>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); const menuWidth = 184; const viewportPadding = 8; const computedLeft = Math.min(Math.max(viewportPadding, rect.right - menuWidth), window.innerWidth - menuWidth - viewportPadding); setActiveHeaderMenu((prev) => prev?.column === col ? null : { column: col, top: rect.bottom + 6, left: computedLeft }); }} style={{ border: "none", background: "transparent", width: 24, height: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#7f8a99", cursor: "pointer", borderRadius: 6 }}><EllipsisVertical size={14} strokeWidth={2.2} /></button>
        {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
      </div>
    );
  }, [activeHeaderMenu, sortConfig]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3; // Changed from 5 to 3

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 2) {
        // Changed from 3 to 2
        for (let i = 1; i <= 3; i++) pageNumbers.push(i); // Changed from 5 to 3
      } else if (currentPage >= totalPages - 1) {
        // Changed from totalPages - 2
        for (let i = totalPages - 2; i <= totalPages; i++) pageNumbers.push(i); // Show last 3 pages
      } else {
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          pageNumbers.push(i); // Show current ± 1
      }
    }

    return pageNumbers;
  };

  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [filteredOrderBook.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Or alternatively, reset when filtered results change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrderBook.length]);

  //----------------------------// End Pagination Functions //----------------------------//

  const convertDate = (dateString) => {
    const timestamp = parseInt(dateString?.match(/\d+/)[0]);
    const dateObject = new Date(timestamp);
    // return `${dateObject.toLocaleDateString()} ${dateObject.toLocaleTimeString()}`;
    return `${dateObject.toLocaleDateString()}`;
  };

  const convertDate1 = (dateString) => {
    const timestamp = parseInt(dateString?.match(/\d+/)[0]);
    const dateObject = new Date(timestamp);
    // return `${dateObject.toLocaleDateString()} ${dateObject.toLocaleTimeString()}`;
    return `${dateObject.toLocaleTimeString()}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    const number = parseFloat(num);
    if (isNaN(number)) return "-";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getStatusClass = (orderStatus) => {
    switch (orderStatus) {
      case "Cancelled":
        return "status_cancel";
      case "Fully Executed":
        return "status_success";
      case "Rejected By 5P":
        return "status_rejected";
      case "Rejected by Exch    ":
        return "status_rejected";
      default:
        return "status_default"; // You can set a default class if needed
    }
  };

  const wrapText = (text, maxChar) => {
    if (!text) return "";

    // Split the text by words
    const words = text.split(/\s+/);

    // Wrap the text after a certain number of characters
    let wrappedText = "";
    let currentLine = "";

    words.forEach((word, index) => {
      if ((currentLine + word).length <= maxChar) {
        currentLine += word + " ";
      } else {
        wrappedText += currentLine.trim() + "<br>";
        currentLine = word + " ";
      }

      // Add a newline at the end if it's the last word
      if (index === words.length - 1) {
        wrappedText += currentLine.trim();
      }
    });

    return wrappedText;
  };

  //----------------------------// Drag-N-Drop Functions //----------------------------//
  // Save column order to PMS (module_name must stay lowercase: "order_history")
  const saveColumnOrder = async (newOrder) => {
    try {
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      if (!userEmail) {
        console.warn("saveColumnOrder: missing email, skipping PMS reorder");
        return;
      }

      const brokerFlag = String(
        localStorage.getItem("broker_type") || sharedData?.broker_type || "",
      ).toLowerCase();
      const pmsBrokerFlag = toPmsBrokerFlag(brokerFlag);
      const columnsPayload = [];
      let sortOrder = 1;
      for (const label of newOrder) {
        if (label === "Actions") continue;
        const columnKey = ORDER_HISTORY_TABLE_COL_TO_PMS_COLUMN_KEY[label];
        if (!columnKey) {
          console.warn(
            "saveColumnOrder: no PMS column_key for table column:",
            label,
          );
          continue;
        }
        columnsPayload.push({ column_key: columnKey, sort_order: sortOrder });
        sortOrder += 1;
      }
      if (columnsPayload.length === 0) return;

      const response = await fetch(`${pmsApiBaseUrl}/api/v1/columns/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          broker_flag: pmsBrokerFlag,
          module_name: "order_history",
          user_id: Number(sharedData?.ID || localStorage.getItem("id") || 0),
          email: userEmail,
          columns: columnsPayload,
        }),
      });
      const result = await response.json();
      console.log("Order history column reorder (PMS):", result);
    } catch (err) {
      console.error("Error saving column order:", err);
    }
  };

  // Drag Start
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);

    const dragPreview = document.createElement("div");
    dragPreview.style.position = "absolute";
    dragPreview.style.top = "-9999px";
    dragPreview.style.padding = "6px 16px";
    dragPreview.style.background = "#C9E0FF";
    dragPreview.style.border = "1px solid #99bde0";
    dragPreview.style.borderRadius = "4px";
    dragPreview.style.color = "#000";
    dragPreview.style.fontSize = "16px";
    dragPreview.style.fontWeight = "bold";
    dragPreview.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
    dragPreview.textContent = columnKey;

    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  // Drag Over
  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
  };


  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();

    const draggedIndex = columnOrder.indexOf(draggedColumn);
    const targetIndex = columnOrder.indexOf(targetColumnKey);

    if (
      draggedIndex === -1 ||
      targetIndex === -1 ||
      draggedIndex === targetIndex
    ) {
      setDraggedColumn(null);
      return;
    }

    const tableContainer = document.querySelector(".table")?.closest("div");
    if (tableContainer) tableContainer.style.overflowY = "hidden";

    const newColumnOrder = [...columnOrder];
    const [movedColumn] = newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, movedColumn);

    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);
    saveColumnOrder(newColumnOrder);

    // Animation wrapped in requestAnimationFrame to prevent ResizeObserver loops
    requestAnimationFrame(() => {
      const cells = document.querySelectorAll(
        `[data-column-id="${movedColumn}"]`,
      );

      // Start animation
      cells.forEach((cell) => cell.classList.add("column-drop-animate"));

      // Remove animation after timeout
      setTimeout(() => {
        cells.forEach((cell) => cell.classList.remove("column-drop-animate"));

        // Temporary highlight of table
        const table = document.querySelector("table");
        table?.classList.add("table-highlight-column");
        setTimeout(() => {
          table?.classList.remove("table-highlight-column");
          if (tableContainer) tableContainer.style.overflowY = "";
        }, 600);
      }, 850);
    });
  };

  // Drag End
  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  //----------------------------// End Drag-N-Drop Functions //----------------------------//

  //----------------------------// Return Main //----------------------------//
  return (
    <>
      {/* <Header /> */}
      <Header tabName={tabName} />
      <div className="wrapper__page pb-5">
        <Sidebar />

        <div className="content_col">
          {/*   <h1>Watchlist</h1> */}

          {/* Watchlist Header with Tabs */}
          <div className="watchlist-header-with-tabs">
            <div className="header-left">
              <h1 className="watchlist-title">{tr("orderHistory")}</h1>
            </div>
          </div>

          {/* Table */}
          <div className="table_main order-history-sticky">
            <div className="table_title_with_view_all_row">
              <h5>{tr("orderHistoryOverTime")}</h5>
            </div>
            <div className="table-responsive">
              {isLoading && (
                <Placeholder.Grid
                  rows={20}
                  columns={9}
                  active
                  style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 12 }}
                />
              )}

              {!isLoading && (
                <table className="table order-history-table">
                  {/* Thead */}
                  <thead>
                    <tr>
                      {visibleColumnOrder.map((col, index) => {
                        const isLastColumn = index === visibleColumnOrder.length - 1;

                        return (
                          <th
                            key={col}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, col)}
                            onDragOver={(e) => handleDragOver(e)}
                            onDrop={(e) => handleDrop(e, col)}
                            onDragEnd={handleDragEnd}
                            data-column-id={col}
                            style={{
                              position: "relative",
                              textAlign:
                                col === "Status"
                                  ? "center"
                                  : col === "Name"
                                    ? "center"
                                    : col === "ScripCode"
                                      ? "center"
                                      : col === "Price"
                                        ? "center"
                                        : col === "Reason"
                                          ? "left"
                                          : col === "Type"
                                            ? "center"
                                            : col === "Qty"
                                              ? "center"
                                              : col === "Date"
                                                ? "center"
                                                : ["OrderStatus"].includes(col)
                                                  ? "center"
                                                  : "center",
                              paddingLeft: col === "Actions" ? 0 : undefined,
                              paddingRight: col === "Actions" ? 0 : undefined,
                              cursor: "grab",
                              paddingRight: 28,
                            }}
                          >
                            {!isLastColumn && renderHeaderMenu(col)}

                            <div
                              className="order-history-th-inner"
                              style={{
                                transition: "all 0.3s ease",
                                transform:
                                  draggedColumn === col ? "scale(1.03)" : undefined,
                                boxShadow:
                                  draggedColumn === col ? "0 0 10px #C9E0FF" : "none",
                                borderRadius:
                                  draggedColumn === col ? "4px" : undefined,
                              }}
                            >
                              {col === "Name" ? (
                                <div
                                  className="table_head_search"
                                  style={{ marginLeft: "15px" }}
                                >
                                  <div style={{ padding: "2px", width: 220 }}>
                                    <button
                                      style={{
                                        padding: "2px 0 2px 5px",
                                        borderRight: "1px solid #e4e4e4ff",
                                      }}
                                      onClick={() =>
                                        searchInputRef.current?.focus()
                                      }
                                    >
                                      <i className="icon-search" />
                                    </button>
                                    <input
                                      type="text"
                                      placeholder={tr("nameSearchHere")}
                                      style={{
                                        paddingLeft: "10px",
                                        marginLeft: "5px",
                                      }}
                                      value={searchQuery}
                                      onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                      }
                                      ref={searchInputRef}
                                    />
                                  </div>
                                </div>
                              ) : col === "Actions" ? (
                                <></>
                              ) : col === "OrderStatus" ? (
                                <>{translateColumnLabel("Order Status", currentLanguage)}</>
                              ) : (
                                translateColumnLabel(col, currentLanguage)
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  {/* Tbody */}
                  <tbody>
                    {sortedOrderHistory.length === 0 ? (
                      <tr>
                        <td
                          colSpan={visibleColumnOrder.length}
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#666",
                          }}
                        >
                          {tr("noOrderDataAvailable")}
                        </td>
                      </tr>
                    ) : currentItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={visibleColumnOrder.length}
                          className="table-empty-state-cell"
                        >
                          {tr("noMatchFound")}
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((order, index) => (
                        <tr key={order.Symbol || index}>
                          {visibleColumnOrder.map((col) => {
                            let content;

                            if (col === "Name") {
                              content = (
                                // <div style={{ paddingLeft: '10px' }} className="main_table_company_cell">
                                //   <h5 className="uppercase">{order.Name || order.ScripName || '-'}</h5>
                                // </div>
                                <div className="main_table_company_cell">
                                  <div style={{ paddingLeft: "15px" }}>
                                    <h5 className="uppercase">
                                      {order.FullName ||
                                        order.Name ||
                                        order.Symbol}
                                    </h5>
                                    <ul
                                      className="company_table_tags"
                                      style={{ paddingLeft: 0 }}
                                    >
                                      <li className="csymbol">
                                        {order.Symbol || order.Name}
                                      </li>
                                      <li className="cbse_nse">
                                        {order.Exch === "N"
                                          ? "NSE"
                                          : order.Exch === "B"
                                            ? "BSE"
                                            : order.Exch === "M"
                                              ? "MCX"
                                              : "BSE"}
                                        {order?.ExchType?.charAt(0) === "C"
                                          ? "-CASH"
                                          : "-DERIVATIVES"}
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              );
                            } else if (col === "Status") {
                              content = (
                                <div>
                                  <span>
                                    {order.shariahCompliantStatus ?? "N/A"}
                                  </span>
                                </div>
                              );
                            } else if (col === "Quantity") {
                              content = (
                                <div>
                                  <span>{order.min_qty ?? order.Qty ?? "-"}</span>
                                </div>
                              );
                            } else if (col === "ScripCode") {
                              content = (
                                <span>
                                  {order.Scripcode ?? order.ScripCode ?? "-"}
                                </span>
                              );
                            } else if (col === "Date") {
                              content = (
                                <div>
                                  <span>{order.orderDate ?? "-"}</span>
                                </div>
                              );
                            } else if (col === "Price") {
                              content = (
                                <div>
                                  <span>
                                    {order.watchlist_price ??
                                      order.CurrentPrice ??
                                      "-"}
                                  </span>
                                </div>
                              );
                            } else if (col === "Actions") {
                              content = <></>;
                            } else if (col === "OrderStatus") {
                              // 🎨 Determine badge styling based on order status
                              let badgeStyle = {
                                padding: "5px 12px",
                                borderRadius: "999px",
                                fontSize: "12px",
                                fontWeight: "600",
                                display: "inline-block",
                                border: "1px solid",
                                letterSpacing: "0.01em",
                              };

                              const status = order.OrderStatus || tr("unknown");
                              const statusLower = status.toLowerCase();

                              // ✅ Status-based color scheme
                              if (
                                statusLower === "success" ||
                                statusLower.includes("order placed") ||
                                statusLower.includes("fully") ||
                                statusLower.includes("complete") ||
                                statusLower.includes("executed") ||
                                statusLower.includes("success")
                              ) {
                                // Success - green
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#dcfce7",
                                  color: "#166534",
                                  borderColor: "#86efac",
                                };
                              } else if (statusLower.includes("partially")) {
                                // Partially executed - teal
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#ccfbf1",
                                  color: "#115e59",
                                  borderColor: "#5eead4",
                                };
                              } else if (statusLower.includes("accepted")) {
                                // Accepted - blue
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#dbeafe",
                                  color: "#1e40af",
                                  borderColor: "#93c5fd",
                                };
                              } else if (statusLower.includes("pending")) {
                                // Pending - amber
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#fef3c7",
                                  color: "#92400e",
                                  borderColor: "#fcd34d",
                                };
                              } else if (
                                statusLower.includes("failed") ||
                                statusLower.includes("rejected") ||
                                statusLower.includes("exchange closed") ||
                                statusLower.includes("invalid")
                              ) {
                                // Failed/Rejected - red
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                  borderColor: "#fca5a5",
                                };
                              } else if (statusLower.includes("cancelled")) {
                                // Cancelled - gray
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#e5e7eb",
                                  color: "#374151",
                                  borderColor: "#cbd5e1",
                                };
                              } else if (statusLower.includes("insufficient")) {
                                // Insufficient funds - orange
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#ffedd5",
                                  color: "#9a3412",
                                  borderColor: "#fdba74",
                                };
                              } else if (statusLower.includes("timeout")) {
                                // Timeout - purple
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#ede9fe",
                                  color: "#5b21b6",
                                  borderColor: "#c4b5fd",
                                };
                              } else if (
                                statusLower.includes("cdsl") ||
                                statusLower.includes("authorization")
                              ) {
                                // CDSL authorization - rose
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#ffe4e6",
                                  color: "#9f1239",
                                  borderColor: "#fda4af",
                                };
                              } else {
                                // Unknown - slate
                                badgeStyle = {
                                  ...badgeStyle,
                                  backgroundColor: "#f1f5f9",
                                  color: "#334155",
                                  borderColor: "#cbd5e1",
                                };
                              }

                              content = <span style={badgeStyle}>{status}</span>;
                            } else if (col === "Reason") {
                              // const reason = order.Reason || tr("unknown");
                              let reason = order.Reason;
                              const status = (order.OrderStatus || "").toLowerCase();
                              const broker = String(localStorage.getItem("broker_type") || "").toLowerCase();
                              
                              if (
                                (broker === "grow" && status === "executed") ||
                                (broker === "5paisa" &&
                                  status.includes("fully executed")) ||
                                (broker === "angelone" &&
                                  (status.includes("complete") ||
                                    status.includes("executed") ||
                                    status === "traded" ||
                                    status === "filled" ||
                                    status === "success"))
                              ) {
                                if (!reason || reason === "-") {
                                  reason = "Order placed successfully";
                                }
                              } else {
                                reason = reason || tr("unknown");
                              }
                              
                              // const status = (
                              //   order.OrderStatus || ""
                              // ).toLowerCase();

                              // 🎨 Determine text color based on order status
                              let textColor = "#495057"; // Default gray

                              if (
                                status.includes("fully") ||
                                status.includes("complete")
                              ) {
                                textColor = "#155724"; // Dark green
                              } else if (status.includes("partially")) {
                                textColor = "#0c5460"; // Teal
                              } else if (status.includes("accepted")) {
                                textColor = "#084298"; // Blue
                              } else if (status.includes("pending")) {
                                textColor = "#856404"; // Yellow/Warning
                              } else if (
                                status.includes("rejected") ||
                                status.includes("exchange closed") ||
                                status.includes("invalid")
                              ) {
                                textColor = "#721c24"; // Red
                              } else if (status.includes("cancelled")) {
                                textColor = "#383d41"; // Dark gray
                              } else if (status.includes("insufficient")) {
                                textColor = "#833d0d"; // Orange
                              } else if (status.includes("timeout")) {
                                textColor = "#3d0a91"; // Purple
                              } else if (
                                status.includes("cdsl") ||
                                status.includes("authorization")
                              ) {
                                textColor = "#c82333"; // Dark red
                              }

                              content = (
                                <span
                                  style={{
                                    color: textColor,
                                    fontSize: "14px",
                                    display: "block",
                                    textAlign: "left",
                                    maxWidth: "100%",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {reason}
                                </span>
                              );
                            } else if (col === "Type") {
                              const typeLabel = (
                                order.order_type ??
                                order.Type ??
                                ""
                              ).toUpperCase();
                              content = (
                                <div>
                                  <span
                                    style={{
                                      color: typeLabel === "BUY" ? "#24AB81" : typeLabel === "SELL" ? "#D25656" : "inherit",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {typeLabel || "-"}
                                  </span>
                                </div>
                              );
                            } else {
                              content = order[col] ?? "-";
                            }

                            return (
                              <td
                                key={col}
                                style={{
                                  textAlign:
                                    col === "Name" || col === "Reason"
                                      ? "left"
                                      : "center",
                                  paddingLeft: col === "Name" ? "10px" : "10px",
                                  padding:
                                    col === "Name"
                                      ? "5px 10px 5px 27px"
                                      : undefined,
                                  ...(col === "Reason"
                                    ? {
                                      maxWidth: 260,
                                      width: 260,
                                      verticalAlign: "middle",
                                    }
                                    : {}),
                                }}
                              >
                                {content}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* {!isLoading && ( */}
            {!isLoading && orderBook.length > 0 && (
              <div className="table__footer_row">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <h6>
                    {indexOfFirstItem + 1} {tr("to")}{" "}
                    {Math.min(indexOfLastItem, filteredOrderBook.length)} {tr("of")}{" "}
                    {filteredOrderBook.length} {tr("items")}
                  </h6>
                  <TablePageSizeSelect
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  />
                </div>

                <nav aria-label="Page navigation example">
                  <ul className="pagination justify-content-center">
                    {/* First Page Button */}
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() => currentPage > 1 && handlePageChange(1)}
                        style={{
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        }}
                        title={tr("firstPage")}
                      >
                        <MdKeyboardDoubleArrowLeft
                          style={{ fontSize: "18px" }}
                        />
                      </a>
                    </li>

                    {/* Previous Page Button */}
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage > 1 && handlePageChange(currentPage - 1)
                        }
                        style={{
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        }}
                        title={tr("previousPage")}
                      >
                        <IoIosArrowBack />
                      </a>
                    </li>

                    {/* Page Numbers */}
                    {getPageNumbers().map((pageNum) => (
                      <li
                        key={pageNum}
                        className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          style={{ cursor: "pointer" }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    ))}

                    {/* Next Page Button */}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage < totalPages &&
                          handlePageChange(currentPage + 1)
                        }
                        style={{
                          cursor:
                            currentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                        }}
                        title={tr("nextPage")}
                      >
                        <IoIosArrowForward />
                      </a>
                    </li>

                    {/* Last Page Button */}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage < totalPages &&
                          handlePageChange(totalPages)
                        }
                        style={{
                          cursor:
                            currentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                        }}
                        title={tr("lastPage")}
                      >
                        <MdKeyboardDoubleArrowRight
                          style={{ fontSize: "18px" }}
                        />
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderHistory;
